import {
  IBayLevelData,
  ISizeSummary,
  IVesselParts,
} from "open-vessel-definition";
import { feetToMillimeters, roundDec } from "@tedivo/tedivo-pure-helpers";

import { BayLcgVcgTcgAndPairings } from "./BayLcgVcgTcgAndPairings/BayLcgVcgTcgAndPairings";
import { IVesselOneBaySlot } from "./types/IVesselOneBaySlot";
import { SVG_WIDTH } from "./consts";
import { calculateSlotsForVesselOne } from "./calculateSlotsForVesselOne";
import { preCalculateCurrentVesselParts } from "./preCalculateCurrentVesselParts";

const FEET_20_IN_MMM = feetToMillimeters(20);

export function generateCoordFunctions({
  sizeSummary,
  baysData,
  vesselPartsData,
  bayLcvVcgTcgAndPairings,
  lcgNormalization = SVG_WIDTH,
  maxHeightAbsolute: maxHeight,
}: IGenerateCoordFunctionsProps): IGeneratedCoordFunctionsResult | undefined {
  const { minLcg, maxLcg, minBottomBase, maxTcg, minTcg, blockBaysAndSizes } =
    bayLcvVcgTcgAndPairings;

  if (maxHeight === undefined || maxTcg === undefined) return undefined; // --> FAST_RETURN: No data to calculate

  const preCalculatedSlots = preCalculateCurrentVesselParts(
    baysData,
    vesselPartsData || [],
  );

  const vOneBaySlots = calculateSlotsForVesselOne(
    preCalculatedSlots,
    bayLcvVcgTcgAndPairings,
    baysData,
  );

  if (vOneBaySlots.length === 0) return undefined; // --> FAST_RETURN: No slots to calculate

  const { sternLength, bowLength } = sternAndBowInMM(sizeSummary.isoBays);

  const allPartsLengthInMeters = maxLcg - minLcg;

  const svgSizeRatio =
    lcgNormalization / (allPartsLengthInMeters + sternLength + bowLength);

  // Find the last bay size in MM
  const lastBaySizeInMM = feetToMillimeters(
    Math.max(
      ...Object.values(
        blockBaysAndSizes[blockBaysAndSizes.length - 1].maxSizes,
      ),
    ),
  );

  const minBottomBaseSafe = minBottomBase === Infinity ? 0 : minBottomBase;

  const scaled = (n: number) => n * svgSizeRatio;

  const addX = scaled(lastBaySizeInMM * 0.5 + sternLength),
    addY = scaled(FEET_20_IN_MMM),
    addZ = scaled(FEET_20_IN_MMM);

  // console.log({
  //   addX,
  //   addY,
  //   addZ,

  //   svgSizeRatio,
  //   lastBaySizeInMM,
  //   sternLength,
  //   bowLength,
  //   allPartsLengthInMeters,
  //   lowestLcgAft: minLcg,
  //   vOneBaySlots,
  // });

  const xRange = roundDec(
    scaled(allPartsLengthInMeters + sternLength + bowLength),
  ); // 2 METER FOR STERN, 30 METERS FOR BOW

  const yRange = roundDec(
    scaled(maxHeight - minBottomBaseSafe + FEET_20_IN_MMM * 2),
  ); // Add 15*2 feet above and below

  const zRange = roundDec(scaled(maxTcg - minTcg + FEET_20_IN_MMM * 2)); // Add 5*2 feet port and starboard

  return {
    addX,
    addY,
    addZ,
    xRange,
    yRange,
    zRange,
    xPos: (lcg: number, d = 2) => roundDec(scaled(lcg - minLcg) + addX, d),
    yPos: (vcg: number, d = 2) =>
      roundDec(scaled(-vcg + maxHeight + FEET_20_IN_MMM), d),
    zPos: (tcg: number, d = 2) =>
      roundDec(scaled(tcg - minTcg + FEET_20_IN_MMM), d),
    scaled,
    svgSizeRatio,
    lastBaySizeInMM: scaled(lastBaySizeInMM),
    sternLength,
    bowLength,
    allPartsLengthInMeters,
    lowestLcgAft: minLcg,
    vOneBaySlots,
  };
}

function sternAndBowInMM(numBays: number) {
  const STERN_TO_LAST_IN_MMM = 0;
  const BOW_TO_FIRST_IN_MMM_MIN = 7000;
  const BOW_TO_FIRST_IN_MMM_MAX = 30000;

  return {
    sternLength: STERN_TO_LAST_IN_MMM,
    bowLength: Math.max(
      Math.min(
        (1 - numBays / 70) * BOW_TO_FIRST_IN_MMM_MIN +
          (numBays / 70) * BOW_TO_FIRST_IN_MMM_MAX,
        BOW_TO_FIRST_IN_MMM_MIN,
      ),
      BOW_TO_FIRST_IN_MMM_MAX,
    ),
  };
}

interface IGenerateCoordFunctionsProps {
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  vesselPartsData: IVesselParts[];
  bayLcvVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  lcgNormalization?: number;
  maxHeightAbsolute: number;
}

interface IGeneratedCoordFunctionsResult {
  addX: number;
  addY: number;
  addZ: number;
  xRange: number;
  yRange: number;
  zRange: number;
  xPos: (lcg: number, d?: number) => number;
  yPos: (vcg: number, d?: number) => number;
  zPos: (tcg: number, d?: number) => number;
  scaled: (n: number) => number;
  svgSizeRatio: number;
  lastBaySizeInMM: number;
  sternLength: number;
  bowLength: number;
  allPartsLengthInMeters: number;
  lowestLcgAft: number;
  vOneBaySlots: Array<IVesselOneBaySlot>;
}
